import { useCallback } from 'react';
import type { SidePanelCommandContext, SidePanelCommandResult } from '../../types';
import { resolveCommandHandler } from '../domainLogic/resolveCommandHandler';
import { sessionAtom } from '../state/atoms';
import { useInternalSidePanelSystem } from './useInternalSidePanelSystem';
import { useSidePanelCommands } from './useSidePanelCommands';
import { useSidePanelState } from './useSidePanelState';

/**
 * Provides owner-command dispatch, close, and layout commands for panel content.
 *
 * @remarks
 * Requires SidePanelSystemProvider. Dispatch resolves the nearest handler in the active session's owner chain.
 * Missing handlers return unhandled; absent or replaced sessions return superseded, including after async completion.
 * Handler errors return failed. Close delegates to the legacy host callback when one is configured.
 * @returns Command functions whose asynchronous results describe the outcome without throwing handler errors.
 */
export function usePanelCommands() {
    const { store, commandHandlers, onSessionClose } = useInternalSidePanelSystem();
    const session = useSidePanelState((state) => state.session);
    const { close, setLayout } = useSidePanelCommands();

    const dispatch = useCallback(
        async <TInput, TOutput = void>(
            commandName: string,
            input: TInput
        ): Promise<SidePanelCommandResult<TOutput>> => {
            if (!session || store.get(sessionAtom)?.sessionId !== session.sessionId) {
                return { status: 'superseded' };
            }

            const resolved = resolveCommandHandler(session.ownerChain, commandName, commandHandlers);
            if (!resolved) {
                return { status: 'unhandled' };
            }

            const context: SidePanelCommandContext = {
                subject: session.subject,
                sessionId: session.sessionId,
                panelId: session.panelId
            };
            try {
                const value = (await resolved.handler(input, context)) as TOutput;
                if (store.get(sessionAtom)?.sessionId !== session.sessionId) {
                    return { status: 'superseded' };
                }
                return { status: 'ok', value, sessionId: session.sessionId };
            } catch (error) {
                return { status: 'failed', error };
            }
        },
        [commandHandlers, session, store]
    );

    const closePanel = useCallback(async () => {
        if (onSessionClose && session) {
            onSessionClose();
            return { status: 'ok', sessionId: session.sessionId } as const;
        }
        const result = await close();
        if (result.status === 'ok') onSessionClose?.();
        return result;
    }, [close, onSessionClose, session]);

    return { dispatch, close: closePanel, setLayout };
}
